import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import dayjs, { Dayjs } from 'dayjs'
import { Typography } from '@mui/material'
import { ProtoSelectStandalone } from 'src/components/flow-editor/ProtoSelectStandalone'
import { ToolbarCheckbox } from 'src/components/payments-ops/ToolbarCheckbox'
import { ToolbarRow } from 'src/components/payments-ops/ToolbarRow'
import { Button } from 'src/components/shared/Button'
import { BasicDatePicker } from 'src/components/shared/form/BasicDatePicker'
import { BasicTextField } from 'src/components/shared/form/BasicTextField'
import { EarningOrderStatus, UsBankDestinationType } from 'src/constants/payments-ops/enums'
import {
  earningOrderStatusLabels,
  usBankDestinationTypeLabels,
} from 'src/constants/payments-ops/labels'
import type { UsBankEarningFilters } from 'src/interfaces/usBankTransactions'
import { isUuid } from 'src/utils/partnerDriver/isUuid'

interface Props {
  filters: UsBankEarningFilters
  onChange(updates: Partial<UsBankEarningFilters>): void
  onClear(): void
}

interface TextFilters {
  contractorId: string
  contractorExternalId: string
  partnerDisplayName: string
}

const ANY_VALUE = ''
const DATE_FORMAT = 'YYYY-MM-DD'
// Typing a year emits a valid Dayjs for every partial year (0002, 0020, 0202, ...).
const MIN_COMMITTED_YEAR = 1900
const CONTRACTOR_ID_ERROR = 'Enter the full contractor UUID'

const parseFilterDate = (value: string | null): Dayjs | null => {
  if (!value) {
    return null
  }

  const date = dayjs(value)

  return date.isValid() ? date : null
}

/**
 * What a picker change means for the URL. An emptied field commits `null`; a valid date with a
 * complete four-digit year commits its `YYYY-MM-DD`; anything else (invalid, partial year) is
 * ignored. Leaving the committed value untouched while the user types is what stops the page
 * re-parsing it into a new Dayjs and MUI resetting the half-typed sections.
 */
const resolveDateChange = (date: Dayjs | null): { shouldCommit: boolean; value: string | null } => {
  if (date === null) {
    return { shouldCommit: true, value: null }
  }

  if (!date.isValid() || date.year() < MIN_COMMITTED_YEAR) {
    return { shouldCommit: false, value: null }
  }

  return { shouldCommit: true, value: date.format(DATE_FORMAT) }
}

const UsBankFiltersToolbarComponent = ({ filters, onChange, onClear }: Props) => {
  const [textFilters, setTextFilters] = useState<TextFilters>({
    contractorId: filters.contractorId ?? '',
    contractorExternalId: filters.contractorExternalId ?? '',
    partnerDisplayName: filters.partnerDisplayName ?? '',
  })

  // Shown only after an Apply with a non-UUID id, so a half-typed id is not flagged mid-typing.
  const [isContractorIdErrorShown, setIsContractorIdErrorShown] = useState<boolean>(false)

  const trimmedContractorId = textFilters.contractorId.trim()
  // The backend's `contractorId` is `@IsUUID()`: anything else 400s the whole list.
  const isContractorIdInvalid = trimmedContractorId !== '' && !isUuid(trimmedContractorId)

  // Clear / Back must reset the draft too.
  useEffect(() => {
    setTextFilters({
      contractorId: filters.contractorId ?? '',
      contractorExternalId: filters.contractorExternalId ?? '',
      partnerDisplayName: filters.partnerDisplayName ?? '',
    })
  }, [filters.contractorExternalId, filters.contractorId, filters.partnerDisplayName])

  const destinationOptions = useMemo(() => {
    const options = Object.values(UsBankDestinationType).map((value) => {
      return { value, label: usBankDestinationTypeLabels[value] }
    })

    return [{ value: ANY_VALUE, label: 'Any destination' }, ...options]
  }, [])

  const orderStatusOptions = useMemo(() => {
    const options = Object.values(EarningOrderStatus).map((value) => {
      return { value, label: earningOrderStatusLabels[value].label }
    })

    return [{ value: ANY_VALUE, label: 'Any status' }, ...options]
  }, [])

  const uploadedFrom = useMemo(() => {
    return parseFilterDate(filters.uploadedFrom)
  }, [filters.uploadedFrom])

  const uploadedTo = useMemo(() => {
    return parseFilterDate(filters.uploadedTo)
  }, [filters.uploadedTo])

  const handleTextChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    if (name === 'contractorId') {
      setIsContractorIdErrorShown(false)
    }

    setTextFilters((previous) => {
      return { ...previous, [name]: value }
    })
  }, [])

  const handleApply = useCallback(() => {
    if (isContractorIdInvalid) {
      setIsContractorIdErrorShown(true)

      return
    }

    onChange({
      contractorId: trimmedContractorId || null,
      contractorExternalId: textFilters.contractorExternalId.trim() || null,
      partnerDisplayName: textFilters.partnerDisplayName.trim() || null,
    })
  }, [isContractorIdInvalid, onChange, textFilters, trimmedContractorId])

  const handleDestinationChange = useCallback(
    (value: string | number) => {
      onChange({
        destinationType: value === ANY_VALUE ? null : (value as UsBankDestinationType),
      })
    },
    [onChange],
  )

  const handleOrderStatusChange = useCallback(
    (value: string | number) => {
      onChange({ orderStatus: value === ANY_VALUE ? null : (value as EarningOrderStatus) })
    },
    [onChange],
  )

  const handleUploadedFromChange = useCallback(
    (date: Dayjs | null) => {
      const result = resolveDateChange(date)

      if (result.shouldCommit) {
        onChange({ uploadedFrom: result.value })
      }
    },
    [onChange],
  )

  const handleUploadedToChange = useCallback(
    (date: Dayjs | null) => {
      const result = resolveDateChange(date)

      if (result.shouldCommit) {
        onChange({ uploadedTo: result.value })
      }
    },
    [onChange],
  )

  // The page skips a no-op clear, so the unapplied text draft is reset here, not via `filters`.
  const handleClear = useCallback(() => {
    setTextFilters({ contractorId: '', contractorExternalId: '', partnerDisplayName: '' })
    setIsContractorIdErrorShown(false)
    onClear()
  }, [onClear])

  const handleActiveOnlyChange = useCallback(
    (activeOnly: boolean) => {
      onChange({ activeOnly })
    },
    [onChange],
  )

  return (
    // Top-aligned: the Contractor ID error must grow the row, not push the inputs around.
    <ToolbarRow align="start">
      <div css={tw`w-[200px]`}>
        <BasicTextField<TextFilters>
          name="contractorId"
          label="Contractor ID"
          placeholder="UUID"
          value={textFilters.contractorId}
          error={isContractorIdErrorShown && isContractorIdInvalid}
          helperText={CONTRACTOR_ID_ERROR}
          onChange={handleTextChange}
        />
      </div>
      <div css={tw`w-[200px]`}>
        <BasicTextField<TextFilters>
          name="contractorExternalId"
          label="Contractor external ID"
          placeholder="Exact match"
          value={textFilters.contractorExternalId}
          onChange={handleTextChange}
        />
      </div>
      <div css={tw`w-[200px]`}>
        <BasicTextField<TextFilters>
          name="partnerDisplayName"
          label="Partner display name"
          placeholder="Contains…"
          value={textFilters.partnerDisplayName}
          onChange={handleTextChange}
        />
      </div>
      <div css={tw`flex w-[180px] flex-col`}>
        <Typography variant="h6" css={tw`mb-1 text-shades-400`}>
          Destination
        </Typography>
        <ProtoSelectStandalone
          value={filters.destinationType ?? ANY_VALUE}
          options={destinationOptions}
          onValueChange={handleDestinationChange}
        />
      </div>
      <div css={tw`flex w-[180px] flex-col`}>
        <Typography variant="h6" css={tw`mb-1 text-shades-400`}>
          Order status
        </Typography>
        <ProtoSelectStandalone
          value={filters.orderStatus ?? ANY_VALUE}
          options={orderStatusOptions}
          onValueChange={handleOrderStatusChange}
        />
      </div>
      {/* `null` keeps the picker controlled-empty; the memoized value only changes reference when
          the committed filter does, so a half-typed field is left alone. */}
      <div css={tw`w-[170px]`}>
        <BasicDatePicker
          label="Uploaded from"
          value={uploadedFrom}
          maxDate={uploadedTo ?? undefined}
          onChange={handleUploadedFromChange}
        />
      </div>
      <div css={tw`w-[170px]`}>
        <BasicDatePicker
          label="Uploaded to"
          value={uploadedTo}
          minDate={uploadedFrom ?? undefined}
          onChange={handleUploadedToChange}
        />
      </div>
      {/* `pt-6` = a field label (20px) plus its 4px gap, so these sit level with the inputs. */}
      <div css={tw`pt-6`}>
        <ToolbarCheckbox
          name="usBankActiveOnly"
          label="Active partners only"
          checked={filters.activeOnly}
          onChange={handleActiveOnlyChange}
        />
      </div>
      <div css={tw`ml-auto flex flex-row gap-2 pt-6`}>
        <Button size="small" color="grey" onClick={handleClear}>
          Clear
        </Button>
        <Button size="small" color="dark" onClick={handleApply} data-testid="us-bank-apply">
          Apply
        </Button>
      </div>
    </ToolbarRow>
  )
}

export const UsBankFiltersToolbar = memo(UsBankFiltersToolbarComponent)
